import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import VideoUrlList from "@/components/admin/VideoUrlList";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { ProfileDetailsEditor, type QAItem, type BookingCTA } from "@/components/athlete/ProfileFieldEditors";
import { publicUrl } from "@/lib/storage";
import { athleteImageUrl } from "@/lib/athleteImage";
import { toast } from "sonner";

type Athlete = {
  id: string;
  slug: string;
  name: string;
  sport: string;
  country: string | null;
  tagline: string | null;
  bio: string | null;
  image_path: string | null;
  gallery_paths: string[];
  video_urls: string[];
  stats: { label: string; value: string }[];
  published: boolean;
  sort_order: number;
  instagram_url: string | null;
  tiktok_url: string | null;
  facebook_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  custom_links: { label: string; url: string }[];
  jersey_number: string | null;
  position_short: string | null;
  position_long: string | null;
  height: string | null;
  hometown: string | null;
  college: string | null;
  occupation: string | null;
  years_playing: number | null;
  throws_hand: string | null;
  high_school: string | null;
  team_logo_path: string | null;
  mantra_quote: string | null;
  support_blurb: string | null;
  qa: QAItem[];
  booking_ctas: BookingCTA[];
  booking_inquiry_email: string | null;
  owner_user_id: string | null;
};

type OwnerInfo = { display_name: string | null };

const empty = (): Omit<Athlete, "id"> => ({
  slug: "",
  name: "",
  sport: "",
  country: "",
  tagline: "",
  bio: "",
  image_path: null,
  gallery_paths: [],
  video_urls: [],
  stats: [],
  published: false,
  sort_order: 0,
  instagram_url: "",
  tiktok_url: "",
  facebook_url: "",
  youtube_url: "",
  linkedin_url: "",
  custom_links: [],
  jersey_number: null,
  position_short: null,
  position_long: null,
  height: null,
  hometown: null,
  college: null,
  occupation: null,
  years_playing: null,
  throws_hand: null,
  high_school: null,
  team_logo_path: null,
  mantra_quote: null,
  support_blurb: null,
  qa: [],
  booking_ctas: [],
  booking_inquiry_email: null,
  owner_user_id: null,
});

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const AdminAthletes = () => {
  const [list, setList] = useState<Athlete[]>([]);
  const [editing, setEditing] = useState<Athlete | (Omit<Athlete, "id"> & { id?: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState<Record<string, OwnerInfo>>({});
  const [linking, setLinking] = useState<Athlete | null>(null);

  const loadOwners = async (rows: Athlete[]) => {
    const ids = Array.from(new Set(rows.map((r) => r.owner_user_id).filter((x): x is string => !!x)));
    if (ids.length === 0) { setOwners({}); return; }
    const { data } = await supabase.from("profiles").select("id, display_name").in("id", ids);
    const map: Record<string, OwnerInfo> = {};
    (data ?? []).forEach((p: any) => { map[p.id] = { display_name: p.display_name }; });
    setOwners(map);
  };

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("athletes")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    const rows = (data as any) ?? [];
    setList(rows);
    setLoading(false);
    loadOwners(rows);
  };

  useEffect(() => {
    load();
  }, []);


  const save = async () => {
    if (!editing) return;
    const payload: any = {
      slug: editing.slug || slugify(editing.name),
      name: editing.name,
      sport: editing.sport,
      country: editing.country,
      tagline: editing.tagline,
      bio: editing.bio,
      image_path: editing.image_path,
      gallery_paths: editing.gallery_paths ?? [],
      video_urls: (editing.video_urls ?? []).filter((u) => u && u.trim()),
      stats: editing.stats,
      published: editing.published,
      sort_order: editing.sort_order,
      instagram_url: editing.instagram_url || null,
      tiktok_url: editing.tiktok_url || null,
      facebook_url: editing.facebook_url || null,
      youtube_url: editing.youtube_url || null,
      linkedin_url: editing.linkedin_url || null,
      custom_links: (editing.custom_links ?? []).filter((l) => l.label?.trim() && l.url?.trim()),
    };
    if (!payload.name || !payload.sport) {
      toast.error("Name and sport are required");
      return;
    }
    if (payload.gallery_paths.length > 10) {
      toast.error("Gallery is limited to 10 images");
      return;
    }
    if (payload.video_urls.length > 3) {
      toast.error("Up to 3 videos");
      return;
    }
    const detailPayload = {
      jersey_number: editing.jersey_number || null,
      position_short: editing.position_short || null,
      position_long: editing.position_long || null,
      height: editing.height || null,
      hometown: editing.hometown || null,
      college: editing.college || null,
      occupation: editing.occupation || null,
      years_playing: editing.years_playing ?? null,
      throws_hand: editing.throws_hand || null,
      high_school: editing.high_school || null,
      team_logo_path: editing.team_logo_path ?? null,
      mantra_quote: editing.mantra_quote || null,
      support_blurb: editing.support_blurb || null,
      qa: (editing.qa ?? []).filter((q) => q.question?.trim() && q.answer?.trim()),
      booking_ctas: (editing.booking_ctas ?? []).filter((c) => c.label?.trim() && c.url?.trim()),
      booking_inquiry_email: editing.booking_inquiry_email || null,
    };
    Object.assign(payload, detailPayload);

    const { error } = (editing as any).id
      ? await supabase.from("athletes").update(payload).eq("id", (editing as any).id)
      : await supabase.from("athletes").insert(payload);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this athlete and all linked causes/products?")) return;
    const { error } = await supabase.from("athletes").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Athletes</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} total</p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
        >
          + New athlete
        </button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {list.map((a) => (
            <div key={a.id} className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-card">
              <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0">
                <img src={athleteImageUrl(a.image_path)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold truncate">{a.name}</p>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${a.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {a.published ? "Live" : "Draft"}
                  </span>
                  {a.owner_user_id ? (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground">
                      Linked{owners[a.owner_user_id]?.display_name ? `: ${owners[a.owner_user_id].display_name}` : ""}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Not linked</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{a.sport} · {a.country}</p>
              </div>
              <button onClick={() => setLinking(a)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">{a.owner_user_id ? "Owner" : "Link user"}</button>
              <button onClick={() => setEditing({ ...a, custom_links: Array.isArray((a as any).custom_links) ? (a as any).custom_links : [], qa: Array.isArray((a as any).qa) ? (a as any).qa : [], booking_ctas: Array.isArray((a as any).booking_ctas) ? (a as any).booking_ctas : [] })} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Edit</button>
              <button onClick={() => remove(a.id)} className="px-4 py-2 rounded-full text-sm font-semibold text-destructive hover:bg-destructive/10">Delete</button>

            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No athletes yet. Create your first one.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{(editing as any).id ? "Edit" : "New"} athlete</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>

            <div className="space-y-4">
              <Group title="Identity & publishing">
                <ImageUploader
                  value={editing.image_path}
                  onChange={(p) => setEditing({ ...editing, image_path: p })}
                  prefix="athletes"
                />
                <Hint>the hero photo (a branded fallback shows when empty).</Hint>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name *">
                    <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inp} maxLength={100} />
                  </Field>
                  <Field label="Slug (URL)">
                    <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder={slugify(editing.name)} className={inp} maxLength={100} />
                  </Field>
                  <Field label="Sport *">
                    <input value={editing.sport} onChange={(e) => setEditing({ ...editing, sport: e.target.value })} className={inp} maxLength={50} />
                  </Field>
                  <Field label="Country">
                    <input value={editing.country ?? ""} onChange={(e) => setEditing({ ...editing, country: e.target.value })} className={inp} maxLength={50} />
                  </Field>
                </div>
                <Hint>the hero name line, the profile URL, and the sport · country line beneath the team.</Hint>
              </Group>

              <Group title="Hero & story">
                <Field label="Tagline">
                  <input value={editing.tagline ?? ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} className={inp} maxLength={200} />
                </Field>
                <p className="text-[11px] text-muted-foreground -mt-2">Short quote shown beneath the hero actions.</p>
                <Field label="Bio">
                  <RichTextEditor value={editing.bio ?? ""} onChange={(v) => setEditing({ ...editing, bio: v })} />
                </Field>
                <Hint>the “About [Name]” section. Hidden entirely when empty.</Hint>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider">Team logo override (shows on hero)</label>
                  <div className="mt-1">
                    <ImageUploader
                      value={editing.team_logo_path ?? null}
                      onChange={(p) => setEditing({ ...editing, team_logo_path: p })}
                      prefix="athletes/team-logo"
                    />
                  </div>
                  <Hint>the logo badge on the hero image.</Hint>
                </div>
              </Group>

              <Group title="Profile details, voice & values, support, Get to Know Her, Work With Me">
                <ProfileDetailsEditor
                  value={editing}
                  onChange={(patch) => setEditing({ ...editing, ...patch })}
                />
              </Group>

              <Group title="Photos & videos">
                <Field label={`Gallery (max 10) — ${(editing.gallery_paths ?? []).length}/10`}>
                  <GalleryUploader
                    value={editing.gallery_paths ?? []}
                    onChange={(paths) => {
                      if (paths.length > 10) { toast.error("Max 10 images"); return; }
                      setEditing({ ...editing, gallery_paths: paths });
                    }}
                    prefix="athletes"
                  />
                </Field>
                <Hint>the “Photos” section.</Hint>

                <Field label={`Videos (max 3) — ${(editing.video_urls ?? []).length}/3`}>
                  <VideoUrlList
                    value={editing.video_urls ?? []}
                    onChange={(urls) => setEditing({ ...editing, video_urls: urls })}
                    max={3}
                  />
                </Field>
                <Hint>the “Videos” section.</Hint>
              </Group>

              <div className="rounded-xl bg-muted/50 border border-border p-4 space-y-3">
                <Hint>the “Connect” section. Hidden when no valid link exists.</Hint>
                <p className="text-xs font-semibold uppercase tracking-wider">Social links (all optional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Instagram">
                    <input value={editing.instagram_url ?? ""} onChange={(e) => setEditing({ ...editing, instagram_url: e.target.value })} className={inp} placeholder="https://instagram.com/..." />
                  </Field>
                  <Field label="TikTok">
                    <input value={editing.tiktok_url ?? ""} onChange={(e) => setEditing({ ...editing, tiktok_url: e.target.value })} className={inp} placeholder="https://tiktok.com/@..." />
                  </Field>
                  <Field label="Facebook">
                    <input value={editing.facebook_url ?? ""} onChange={(e) => setEditing({ ...editing, facebook_url: e.target.value })} className={inp} placeholder="https://facebook.com/..." />
                  </Field>
                  <Field label="YouTube">
                    <input value={editing.youtube_url ?? ""} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} className={inp} placeholder="https://youtube.com/@..." />
                  </Field>
                  <Field label="LinkedIn">
                    <input value={editing.linkedin_url ?? ""} onChange={(e) => setEditing({ ...editing, linkedin_url: e.target.value })} className={inp} placeholder="https://linkedin.com/in/..." />
                  </Field>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider">
                    Custom links (max 3) — {(editing.custom_links ?? []).length}/3
                  </label>
                  <div className="space-y-2 mt-2">
                    {(editing.custom_links ?? []).map((l, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          value={l.label}
                          onChange={(e) => {
                            const next = [...(editing.custom_links ?? [])];
                            next[i] = { ...l, label: e.target.value };
                            setEditing({ ...editing, custom_links: next });
                          }}
                          placeholder="Label (e.g. Website)"
                          className={inp}
                          maxLength={40}
                        />
                        <input
                          value={l.url}
                          onChange={(e) => {
                            const next = [...(editing.custom_links ?? [])];
                            next[i] = { ...l, url: e.target.value };
                            setEditing({ ...editing, custom_links: next });
                          }}
                          placeholder="https://"
                          className={inp}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              custom_links: (editing.custom_links ?? []).filter((_, j) => j !== i),
                            })
                          }
                          className="px-3 text-destructive"
                          aria-label="Remove link"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {(editing.custom_links ?? []).length < 3 && (
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            custom_links: [...(editing.custom_links ?? []), { label: "", url: "" }],
                          })
                        }
                        className="text-sm text-primary font-semibold"
                      >
                        + Add link
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <Group title="Stats">
                <label className="text-xs font-semibold uppercase tracking-wider">Stats (max 6)</label>
                <Hint>the “By the Numbers” section. Each stat needs both a label and a value.</Hint>
                <div className="space-y-2 mt-2">
                  {editing.stats.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={s.label} onChange={(e) => {
                        const next = [...editing.stats]; next[i] = { ...s, label: e.target.value };
                        setEditing({ ...editing, stats: next });
                      }} placeholder="Label" className={inp} maxLength={30} />
                      <input value={s.value} onChange={(e) => {
                        const next = [...editing.stats]; next[i] = { ...s, value: e.target.value };
                        setEditing({ ...editing, stats: next });
                      }} placeholder="Value" className={inp} maxLength={30} />
                      <button onClick={() => setEditing({ ...editing, stats: editing.stats.filter((_, j) => j !== i) })} className="px-3 text-destructive">✕</button>
                    </div>
                  ))}
                  {editing.stats.length < 6 && (
                    <button onClick={() => setEditing({ ...editing, stats: [...editing.stats, { label: "", value: "" }] })} className="text-sm text-primary font-semibold">
                      + Add stat
                    </button>
                  )}
                </div>
              </Group>

              <Group title="Admin-only settings">
                <div className="grid grid-cols-2 gap-3 items-end">
                  <Field label="Sort order">
                    <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} className={inp} />
                  </Field>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer">
                    <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-5 h-5 accent-primary" />
                    <span className="font-semibold text-sm">Published (visible on site)</span>
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground">Not shown to fans. Controls listing order and whether the profile is public.</p>
              </Group>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}

      {linking && (
        <LinkOwnerDialog
          athlete={linking}
          onClose={() => setLinking(null)}
          onSaved={() => { setLinking(null); load(); }}
        />
      )}
    </AdminLayout>
  );
};

type UserRow = { id: string; email: string; display_name: string | null };

const LinkOwnerDialog = ({
  athlete,
  onClose,
  onSaved,
}: {
  athlete: Athlete;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      const { data, error } = await supabase.rpc("admin_search_users" as any, { q });
      setSearching(false);
      if (error) { toast.error(error.message); return; }
      setResults((data as any) ?? []);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const setOwner = async (userId: string | null) => {
    setSaving(true);
    const { error } = await supabase.rpc("admin_set_athlete_owner" as any, {
      _athlete_id: athlete.id,
      _new_owner: userId,
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(userId ? "Linked" : "Unlinked");
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-[1.5rem] shadow-bold max-w-md w-full p-6 my-8">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-display text-2xl">Link user</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Athlete: <span className="font-semibold text-foreground">{athlete.name}</span>
        </p>

        {athlete.owner_user_id && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl bg-muted p-3">
            <span className="text-sm">Currently linked to a user.</span>
            <button
              disabled={saving}
              onClick={() => setOwner(null)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-destructive hover:bg-destructive/10"
            >
              Unlink
            </button>
          </div>
        )}

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name…"
          className={inp}
          autoFocus
        />

        <div className="mt-3 max-h-72 overflow-y-auto space-y-1">
          {searching && <p className="text-sm text-muted-foreground px-2 py-1">Searching…</p>}
          {!searching && results.length === 0 && (
            <p className="text-sm text-muted-foreground px-2 py-1">No users found.</p>
          )}
          {results.map((u) => (
            <button
              key={u.id}
              disabled={saving}
              onClick={() => setOwner(u.id)}
              className="w-full text-left px-3 py-2 rounded-xl hover:bg-muted transition-colors"
            >
              <p className="text-sm font-semibold truncate">{u.email}</p>
              {u.display_name && <p className="text-xs text-muted-foreground truncate">{u.display_name}</p>}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
};


const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

/** One editor group, mirroring a section of the public profile. */
const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl border border-border p-4 space-y-3">
    <h3 className="font-display text-lg leading-none">{title}</h3>
    {children}
  </section>
);

/** Tells the admin exactly where a field shows up for fans. */
const Hint = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] text-muted-foreground mt-1">Appears on public profile as… {children}</p>
);

export default AdminAthletes;
